import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { useApi } from '../services/apiService';
import {
  formatMoney,
  formatShortDate,
  formatSignedMoney,
} from '../utils/format';
import './TransactionList.css';
import CreditTransactionDropdown from './CreditTransactionDropdown';

type Transaction = {
  id: string;
  date: string;
  amount: number;
  description: string;
  relatedTransactions: Transaction[];
};

type TransactionListProps = {
  category: string;
};

const TransactionList: React.FC<TransactionListProps> = ({ category }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);
  const { getTransactionsByCategory, createRelatedTransaction } = useApi();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const fetchTransactions = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      setError(null);
      try {
        const data = await getTransactionsByCategory(category);
        setTransactions(data);
      } catch (err) {
        setError('Failed to fetch transactions.');
      } finally {
        setLoading(false);
      }
    },
    [category],
  );

  useEffect(() => {
    setStatus(null);
    setOpenDropdown(null);
    if (category) {
      fetchTransactions();
    }
  }, [category]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('.transaction-link-button')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLinkClick = (transactionId: string) => {
    setOpenDropdown(openDropdown === transactionId ? null : transactionId);
  };

  const handleLinkTransaction = async (
    parentTransactionId: string,
    creditTransactionId: string,
  ) => {
    try {
      await createRelatedTransaction(parentTransactionId, creditTransactionId);
      setStatus({ kind: 'success', message: 'Transaction linked.' });
      setOpenDropdown(null);
      await fetchTransactions(false);
    } catch (err) {
      console.error('Failed to link transactions:', err);
      setStatus({
        kind: 'error',
        message: 'Failed to link transactions. Please try again.',
      });
    }
  };

  const categoryTotal = transactions.reduce(
    (sum, t) => sum + Number(t.amount),
    0,
  );

  return (
    <section
      className="card transaction-list"
      aria-labelledby="category-transactions-title"
    >
      <div className="section-header">
        <div>
          <h2 id="category-transactions-title" className="card-title">
            {category} transactions
          </h2>
          <p className="card-subtitle">
            Link a refund or reimbursement to the purchase it offsets
          </p>
        </div>
        {!loading && !error && transactions.length > 0 && (
          <span className="transaction-list-total num">
            {formatMoney(categoryTotal, 0)} this month
          </span>
        )}
      </div>

      <div aria-live="polite">
        {status && (
          <p className={`transaction-status is-${status.kind}`}>
            {status.message}
          </p>
        )}
      </div>
      {loading && <p className="muted">Loading transactions...</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && transactions.length === 0 && (
        <p className="muted">No transactions in this category.</p>
      )}

      {!loading && !error && (
        <ul className="transaction-list-items">
          {transactions.map((transaction) => {
            const amount = Number(transaction.amount);
            const isOpen = openDropdown === transaction.id;
            return (
              <li key={transaction.id} className="transaction-item">
                <div className="transaction-item-grid">
                  <span className="transaction-item-date">
                    {formatShortDate(transaction.date)}
                  </span>
                  <span className="transaction-item-description">
                    {transaction.description}
                  </span>
                  <span
                    className={`transaction-item-amount num${amount >= 0 ? ' is-credit' : ''}`}
                  >
                    {formatSignedMoney(amount)}
                  </span>
                  <button
                    className={`transaction-link-button${isOpen ? ' is-open' : ''}`}
                    onClick={() => handleLinkClick(transaction.id)}
                    aria-expanded={isOpen}
                    aria-label={
                      isOpen
                        ? 'Close link menu'
                        : `Link a credit to ${transaction.description}`
                    }
                  >
                    <Icon name="link" size={16} />
                  </button>
                </div>
                {transaction.relatedTransactions &&
                  transaction.relatedTransactions.length > 0 && (
                    <ul className="related-transactions">
                      {transaction.relatedTransactions.map((related) => (
                        <li
                          key={related.id}
                          className="related-transaction-item"
                        >
                          <Icon name="cornerDownRight" size={14} />
                          <span>
                            Linked: {formatShortDate(related.date)} ·{' '}
                            {related.description}
                          </span>
                          <span className="related-transaction-amount num">
                            {formatSignedMoney(Number(related.amount))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                {isOpen && (
                  <div ref={dropdownRef} className="transaction-dropdown">
                    <CreditTransactionDropdown
                      parentTransactionId={transaction.id}
                      onLinkTransaction={handleLinkTransaction}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default TransactionList;
